
import { toTitleCase } from "../../../utils/common";

export function layoutLeftToRight(model, type, extraCounts, opts = {}) {
  const {
    padding = 40,
    nodeWidth = 220,
    nodeHeight = 72,
    colGap = 80,
    rowGap = 24,
  } = opts;

  // ✅ Static stage order
  const stageOrder = [
    "Internal Submission",
    "Client Submissions",
    "Aggrements",
    "Interview Round 1",
    "Interview Round 2",
    "Interview Round 3",
    "Placements",
  ];

  const extraMap = Array.isArray(extraCounts)
    ? extraCounts.reduce((acc, item) => {
      const id = item?.addedBy?.uiid;
      if (id != null) {
        acc[id] = item;
      }
      return acc;
    }, {})
    : {};

  const employees = Array.isArray(model)
    ? model.map((item) => {
      const submissions = item?.submissions || {};
      const empId = item?.addedBy?.uiid;

      const extra = extraMap?.[empId] || {};

      return {
        employeeId: empId,
        employeeName: `${toTitleCase(item?.addedBy?.firstName) || ""} ${item?.addedBy?.lastName || ""
          }`.trim(),

        jobsOpenCount: item?.jobsAdded ?? 0,
        withSubmissions: item?.jobsWithSubmissions ?? 0,
        withoutSubmissions: item?.jobsWithoutSubmissions ?? 0,

        oldWithSubmissions: extra?.oldJobsWithSubmissions ?? 0,
        oldWithoutSubmissions: extra?.oldJobsWithoutSubmissions ?? 0,

        stageCounts: {
          "Internal Submission": submissions.internalSubmissions ?? 0,
          "Client Submissions": submissions.clientSubmissions ?? 0,
          "Aggrements": submissions.aggrements ?? 0,
          "Interview Round 1": submissions.interviewRound1 ?? 0,
          "Interview Round 2": submissions.interviewRound2 ?? 0,
          "Interview Round 3": submissions.interviewRound3 ?? 0,
          "Placements": submissions.placed ?? 0,
        },
      };
    })
    : [];

  employees.sort((a, b) => (b.jobsOpenCount ?? 0) - (a.jobsOpenCount ?? 0));

  const nodes = [];
  const edges = [];

  const empCount = employees.length;

  const totalHeight =
    empCount > 0
      ? padding * 2 + empCount * nodeHeight + (empCount - 1) * rowGap
      : padding * 2 + nodeHeight;

  const rootX = padding;
  const rootY =
    empCount > 0
      ? padding + ((empCount - 1) * (nodeHeight + rowGap)) / 2
      : padding;

  // Root node
  nodes.push({
    id: "root",
    type: "root",
    label: type == "recruit" ? "Total Submissions" : "Jobs Opened",
    value: model?.[0]?.totalJobsCount ?? 0,
    x: rootX,
    y: rootY,
    width: nodeWidth,
    height: nodeHeight,
    payload: { type: "root" },
  });

  const empX = rootX + nodeWidth + colGap;

  // Employees + stages
  employees.forEach((emp, idx) => {
    const y = padding + idx * (nodeHeight + rowGap);
    const empId = String(emp?.employeeId ?? "");
    const empNodeId = `emp:${empId}:${idx}`;

    // Employee node
    nodes.push({
      id: empNodeId,
      type: "employee",
      modelType : type,
      label: emp?.employeeName || empId,
      value: emp?.jobsOpenCount ?? 0,
      withSubmissions: emp?.withSubmissions,
      withoutSubmissions: emp?.withoutSubmissions,
      oldWithSubmissions: emp?.oldWithSubmissions,
      oldWithoutSubmissions: emp?.oldWithoutSubmissions,
      x: empX,
      y,
      width: nodeWidth,
      height: nodeHeight,
      payload: { type: "employee", employeeId: empId },
    });


    edges.push({
      id: `e:root->${empNodeId}`,
      from: "root",
      to: empNodeId,
    });

    const stageBaseX = empX + nodeWidth + colGap;

    // Only non-zero stages
    const stageNames = stageOrder
    stageNames.forEach((stageName, sIdx) => {
      const stageNodeId = `stage:${empId}:${stageName}:${idx}`;
      const x = stageBaseX + sIdx * (nodeWidth + colGap);
      const count = emp?.stageCounts?.[stageName] ?? 0;

      nodes.push({
        id: stageNodeId,
        type: "stage",
        label: stageName,
        value: count,
        x,
        y,
        width: nodeWidth,
        height: nodeHeight,
        payload: { type: "stage", employeeId: empId, stageName },
      });

      const prevId =
        sIdx === 0
          ? empNodeId
          : `stage:${empId}:${stageNames[sIdx - 1]}`;

      edges.push({
        id: `e:${prevId}->${stageNodeId}`,
        from: prevId,
        to: stageNodeId,
      });
    });
  });

  //  Width based on full stage order
  const stageColCount = stageOrder.length;

  const lastColX =
    empX +
    nodeWidth +
    colGap +
    Math.max(0, stageColCount - 1) * (nodeWidth + colGap);

  const contentWidth = lastColX + nodeWidth + padding;
  const contentHeight = totalHeight;

  return {
    nodes,
    edges,
    bounds: {
      x: 0,
      y: 0,
      width: contentWidth,
      height: contentHeight,
    },
  };
}